import { Form, Formik } from "formik";
import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import AddButton from "../../buttons/button";
import InputField from "../../forms/input-field";
import TabsComponent from "../../helper-components/tabs-component";

import { useDispatch } from "react-redux";
import { StorePaymentCreateAction } from "../../../redux/actions/store-action";
import { api } from "../../../redux/apis/api";
import successToast from "../../helper-components/success-toast";
import errorToast from "../../helper-components/error-toast";
import SelectField from "../../forms/select-field";
import StorePayerInfoTab from "./payer-info";
import StorePayeeInfoTab from "./payee-info";
import StoreGstInfoTab from "./gst-info";
import StoreRemarksInfoTab from "./remarks-info";
import StorePaymentHistoryTab from "./payment-history";
import MultipleImagesField from "../../forms/multiple-images-field";
import ImagePreviewModal from "../../helper-components/image-preview-modal";
import ApproveDeclineActions from "../approve-decline-actions";

const StorePaymentTab = ({
  id,
  setStoreData,
  storeData,
  initialValue,
  permissionFn = () => {},
  disabled = false,
  // Reports a finished step to the tab container, which unlocks the next tab.
  onProgress = () => {},
}) => {
  const [currentTab, setCurrentTab] = useState("Payer Info");
  const [accountDetails, setAccountDetails] = useState(null);
  const [storeTypeMap, setStoreTypeMap] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [previewSrc, setPreviewSrc] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    api
      .get("/fofo/storeAccountDetails/getAll")
      .then((res) => {
        const first = res.data?.data?.[0];
        if (first) setAccountDetails(first);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    api
      .get("/fofo/storeType/getAll")
      .then((res) => {
        const list = res.data?.data ?? [];
        const map = {};
        list.forEach((t) => { map[t.store_type] = t; });
        setStoreTypeMap(map);
      })
      .catch(() => {});
  }, []);

  const franchiseFee =
    storeData?.address?.franchise_fee ??
    storeTypeMap[storeData?.address?.store_type]?.franchidse_fee ??
    "";

  // Amount to be Paid is derived; a stored value wins, otherwise recompute.
  const storedAmountToBePaid = initialValue?.amount_to_be_paid;
  const hasStoredAmountToBePaid =
    storedAmountToBePaid !== null &&
    storedAmountToBePaid !== undefined &&
    storedAmountToBePaid !== "";
  const totalAmountValue =
    parseFloat(initialValue?.total_amount ?? franchiseFee) || 0;
  const totalPaidValue = parseFloat(initialValue?.total_paid) || 0;

  // Paid so far = sum of saved payment records, the only figure that survives reload.
  const paymentHistory = Array.isArray(initialValue?.payment)
    ? initialValue.payment
    : [];
  // /payment/add has no update mode, so re-saves duplicate rows — count each txn once.
  const countedTransactions = new Set();
  const paidSoFar = paymentHistory.reduce((sum, p) => {
    const amount = parseFloat(p?.payment_amount ?? p?.amount) || 0;
    const key =
      p?.transaction_id != null && p.transaction_id !== ""
        ? `txn:${p.transaction_id}`
        : `amt:${amount}|${p?.payment_date ?? p?.payment_recevie_time ?? ""}`;
    if (countedTransactions.has(key)) return sum;
    countedTransactions.add(key);
    return sum + amount;
  }, 0);
  const paidValue = paidSoFar > 0 ? paidSoFar : totalPaidValue;
  const derivedAmountToBePaid =
    totalAmountValue > 0
      ? // Round to paise to avoid float noise.
        Math.round(Math.max(totalAmountValue - paidValue, 0) * 100) / 100
      : "";

  const initialValues = {
    store_id: id ?? null,
    is_payment_terms_approve: initialValue?.is_payment_terms_approve ?? false,
    // The franchise-fee decision is the one the approve/reject endpoints record.
    // (The backend spells "francise"; sent verbatim.)
    is_francise_fee_approved: initialValue?.is_francise_fee_approved ?? false,
    is_francise_fee_rejected: initialValue?.is_francise_fee_rejected ?? false,
    store_type: initialValue?.store_type ?? "",
    total_amount: initialValue?.total_amount ?? franchiseFee ?? "",
    total_paid: initialValue?.total_paid ?? "",
    amount_to_be_paid: hasStoredAmountToBePaid
      ? storedAmountToBePaid
      : derivedAmountToBePaid,
    payment_method: initialValue?.payment_method ?? "",
    payment_amount: initialValue?.payment_amount ?? null,
    // Per-instalment, like the receipt — a new payment needs its own reference,
    // so the previous transaction's ID is never carried into the form.
    transaction_id: "",
    payment_status: initialValue?.payment_status ?? "",
    payment_recevie_time: (() => {
      const raw = initialValue?.payment_recevie_time ?? initialValue?.payment_date ?? null;
      if (!raw) return null;
      const d = new Date(raw);
      return isNaN(d) ? null : d.toISOString().slice(0, 16);
    })(),
    // Deliberately empty, never seeded from the record. This field is where the
    // admin attaches receipts for the instalment being entered; everything already
    // on file — including what the end user uploaded from the mobile app — belongs
    // to its own transaction and is shown in the Payment History table.
    payment_receipt: [],
    payment_remarks: initialValue?.payment_remarks ?? "",
    payer_name_as_per_bank: initialValue?.payer_name_as_per_bank ?? "",
    payer_bank_name: initialValue?.payer_bank_name ?? "",
    payer_bank_account_no: initialValue?.payer_bank_account_no ?? "",
    payer_remaining_amount: initialValue?.payer_remaining_amount ?? "",
    payee_name_as_per_bank: initialValue?.payee_name_as_per_bank ?? accountDetails?.account_name ?? "",
    payee_bank_name: initialValue?.payee_bank_name ?? accountDetails?.bank_name ?? "",
    payee_bank_account_no: initialValue?.payee_bank_account_no ?? accountDetails?.account_number ?? "",
    isfc_code:
      initialValue?.isfc_code ||
      initialValue?.ifsc_code ||
      accountDetails?.ifsc_code ||
      accountDetails?.isfc_code ||
      "",
    extra_payee: initialValue?.extra_payee ?? "",
  };

  const validationSchema = Yup.object().shape({
    payer_name_as_per_bank: Yup.string().required("Payer name is required"),
    payer_bank_name: Yup.string().required("Bank name is required"),
    payer_bank_account_no: Yup.string()
      .required("Bank account number is required")
      .matches(/^[0-9]{9,18}$/, "Invalid bank account number"),
    payer_remaining_amount: Yup.number()
      .required("Remaining amount is required")
      .positive("Remaining amount must be positive"),
    payee_name_as_per_bank: Yup.string().required("Payee name is required"),
    payee_bank_name: Yup.string().required("Bank name is required"),
    payee_bank_account_no: Yup.string()
      .required("Bank account number is required")
      .matches(/^[0-9]{9,18}$/, "Invalid bank account number"),
    isfc_code: Yup.string().required("IFSC code is required"),
    extra_payee: Yup.string().required("Required"), // No validation, optional field
    gst_number: Yup.string()
      .required("GST number is required")
      .matches(
        /^([0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1})?$/,
        "Invalid GST number"
      ),
  });

  const onSubmit = (values) => {
    if (!id) {
      errorToast("Please save Store Details first");
      return;
    }
    if (paymentApproved) {
      errorToast("This payment has already been approved");
      return;
    }
    if (fullyPaid) {
      errorToast("The total amount has already been paid in full");
      return;
    }
    const isTransactionOptional = ["cheque", "dd"].includes(values.payment_method);
    if (!isTransactionOptional && !values.transaction_id) {
      errorToast("Transaction ID is required");
      return;
    }
    if (!values.payment_amount) {
      errorToast("Payment Amount is required");
      return;
    }
    const payload = {
      store_id: id,
      transaction_id: isTransactionOptional ? 1 : values.transaction_id,
      payment_amount: values.payment_amount,
      payment_method: values.payment_method,
      payment_status: values.payment_status,
      payment_remarks: values.payment_remarks,
      // A single receipt is sent as it always was; only a genuine multi-upload
      // sends an array, so existing single-receipt records are unaffected.
      payment_receipt:
        values.payment_receipt?.length === 1
          ? values.payment_receipt[0]
          : values.payment_receipt ?? [],
      payer_name_as_per_bank: values.payer_name_as_per_bank,
      payer_bank_name: values.payer_bank_name,
      payer_bank_account_no: values.payer_bank_account_no,
      isfc_code: values.isfc_code,
    };
    setIsSaving(true);
    dispatch(
      StorePaymentCreateAction(payload, (data) => onSuccess(data, values), onError)
    );
  };

  const onSuccess = (data, submittedValues = {}) => {
    setIsSaving(false);

    // Append the saved instalment; updater form so consecutive saves accumulate.
    setStoreData((prev) => {
      const prevPayment = prev?.payment;
      const existingHistory = Array.isArray(prevPayment?.payment)
        ? prevPayment.payment
        : [];
      const responseHistory = Array.isArray(data?.data?.payment)
        ? data.data.payment
        : null;
      // The save response carries no row of its own, so the appended line is built
      // from what was just submitted. Appending only the amount left every other
      // column showing "--" until the page was reloaded.
      const submittedRow = {
        payment_amount: submittedValues?.payment_amount,
        payment_method: submittedValues?.payment_method,
        transaction_id: submittedValues?.transaction_id,
        payment_status: submittedValues?.payment_status,
        payment_remarks: submittedValues?.payment_remarks,
        payment_receipt: submittedValues?.payment_receipt,
        payment_recevie_time:
          submittedValues?.payment_recevie_time || new Date().toISOString(),
      };
      const updatedHistory =
        responseHistory ?? [...existingHistory, submittedRow];

      // Keep previous values underneath — the form reinitialises from this object.
      return {
        ...prev,
        payment: {
          ...prevPayment,
          ...submittedValues,
          ...data?.data,
          payment: updatedHistory,
          // Cleared for the next instalment; balance recomputed from history above.
          total_paid: "",
          amount_to_be_paid: "",
          // The uploaded receipts now belong to the row just appended, so the
          // field empties ready for the next instalment.
          payment_receipt: [],
          transaction_id: "",
        },
      };
    });
    successToast(data?.data?.message);
  };
  const onError = (data) => {
    setIsSaving(false);
    errorToast(data?.data?.message);
  };

  // Once the franchise fee is approved the entry fields come off — the decision
  // is made, so only the sub-tabs (payer, payee, GST, remarks, history) remain.
  // A declined payment goes back to being editable so it can be corrected.
  //
  // Held in state rather than read straight off `initialValue`: a decision made
  // here updates the form, not the stored record, so reading the prop left the
  // fields hidden until the page was revisited. The approve/decline handler sets
  // this from the server's response, and the effect re-syncs it whenever a fresh
  // record arrives.
  const [paymentApproved, setPaymentApproved] = useState(
    !!initialValue?.is_francise_fee_approved
  );

  useEffect(() => {
    setPaymentApproved(!!initialValue?.is_francise_fee_approved);
  }, [initialValue?.is_francise_fee_approved]);

  // Nothing further can be taken once the full amount is settled.
  const totalAmountDue =
    parseFloat(initialValue?.total_amount ?? franchiseFee) || 0;
  const alreadyPaid = (
    Array.isArray(initialValue?.payment) ? initialValue.payment : []
  ).reduce(
    (sum, row) =>
      sum + (parseFloat(row?.payment_amount ?? row?.amount ?? 0) || 0),
    0
  );
  const fullyPaid = totalAmountDue > 0 && alreadyPaid >= totalAmountDue;

  const entryDisabled = disabled || paymentApproved || fullyPaid;

  return (
    <>
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      // validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ values, setFieldValue, isSubmitting, errors, submitForm, initialValues: savedValues }) => {
        
        
        return (
          <Form style={{ height: "calc(100% - 5rem)" }} className="pt-5">
            <div className="flex flex-col justify-between h-full ">
              <div>
                {paymentApproved && (
                  <div className="px-5 xl:px-10 pt-2 pb-4">
                    <p className="text-[length:var(--input-label-font-size)] text-[var(--input-label-color)]">
                      This payment has been approved. The entry fields are closed —
                      the details below remain available.
                    </p>
                  </div>
                )}
                {fullyPaid && !paymentApproved && (
                  <div className="px-5 xl:px-10 pt-2 pb-4">
                    <p className="text-[length:var(--input-label-font-size)] text-[var(--input-label-color)]">
                      The total amount has been paid in full. No further payment can
                      be recorded.
                    </p>
                  </div>
                )}
                {/* Entry fields disappear once the decision is made; a declined
                    payment stays editable so it can be corrected. */}
                {!paymentApproved && (
                <div className="w-full 2xl:w-[35%] lg:w-[60%] px-5 xl:px-10">
                  <InputField
                    isDisabled={disabled}
                    type="text"
                    name="store_type"
                    placeholder="Enter Store Type"
                    label="Store Type"
                    // isRequired={true}
                    labelWidth="11rem"
                  />
                  <InputField
                    isDisabled={true}
                    type="number"
                    name="total_amount"
                    placeholder="Auto-filled from Franchise Fee"
                    label="Total Amount"
                    labelWidth="11rem"
                  />
                  <InputField
                    isDisabled={disabled}
                    type="number"
                    name="total_paid"
                    placeholder="Enter Total Paid"
                    label="Total Paid"
                    labelWidth="11rem"
                    handleChange={(e, field) => {
                      const totalPaid = parseFloat(e.target.value) || 0;
                      const totalAmount = parseFloat(storeData?.address?.franchise_fee ?? values.total_amount) || 0;
                      if (totalPaid > totalAmount) {
                        errorToast("Total Paid cannot exceed Total Amount");
                        return;
                      }
                      field.onChange(e);
                      setFieldValue("amount_to_be_paid", Math.max(totalAmount - totalPaid, 0));
                    }}
                  />
                  <InputField
                    isDisabled={true}
                    type="number"
                    name="amount_to_be_paid"
                    placeholder="Auto-calculated"
                    label="Amount to be Paid"
                    labelWidth="11rem"
                  />
                  <SelectField
                    isDisabled={disabled}
                    placeholder={"Select payment method"}
                    options={paymentTypeList}
                    label={"Select Payment Method"}
                    id={"payment_method"}
                    name="payment_method"
                    isSearchable={true}
                    errMsgContainerClass="ps-2"
                    labelWidth="11rem"
                  />
                  <InputField
                    isDisabled={disabled}
                    type="number"
                    name="payment_amount"
                    placeholder="Enter Payment Amount"
                    label="Payment Amount"
                    labelWidth="11rem"
                  />
                  <InputField
                    isDisabled={disabled}
                    type="text"
                    name="transaction_id"
                    placeholder="Enter Transaction ID"
                    label="Transaction ID"
                    labelWidth="11rem"
                  />
                  <SelectField
                    isDisabled={disabled}
                    placeholder="Select Payment Status"
                    options={paymentStatusList}
                    label="Payment Status"
                    id="payment_status"
                    name="payment_status"
                    labelWidth="11rem"
                  />
                  <InputField
                    isDisabled={disabled}
                    type="text"
                    name="payment_remarks"
                    placeholder="Enter Payment Remarks"
                    label="Payment Remarks"
                    labelWidth="11rem"
                  />
                  <InputField
                    isDisabled={disabled}
                    type="datetime-local"
                    name="payment_recevie_time"
                    placeholder="Enter Payment Receive Time"
                    label="Payment Receive Time"
                    labelWidth="11rem"
                  />
                  {/* <InputField
                    type="date"
                    name="addres_remarks"
                    placeholder=""
                    label="Payment Receive Date"
                    labelWidth="11rem"
                  /> */}

                  <MultipleImagesField
                    isDisabled={disabled}
                    name="payment_receipt"
                    label="Payment Receipt"
                    labelWidth="11rem"
                    setloading={setReceiptLoading}
                  />
                  {receiptLoading && (
                    <p className="ms-[11rem] text-[length:var(--input-place-font-size)] text-[var(--input-place-color)]">
                      Uploading...
                    </p>
                  )}
                  {/* Receipts already on the record — several arrive from the
                      mobile app, so they are listed rather than replaced. */}
                  {Array.isArray(values.payment_receipt) &&
                    values.payment_receipt.length > 0 && (
                    <div className="flex flex-wrap gap-3 ms-[11rem] mb-2">
                      {values.payment_receipt.map((src, index) => (
                        <div key={index} className="relative group">
                          <img
                            alt={`Payment receipt ${index + 1}`}
                            src={src}
                            onClick={() => setPreviewSrc(src)}
                            className="rounded-md cursor-pointer bg-EBEBEB"
                            style={{ height: "5rem", width: "5rem", objectFit: "cover" }}
                          />
                          {!disabled && (
                            <button
                              type="button"
                              onClick={() =>
                                setFieldValue(
                                  "payment_receipt",
                                  values.payment_receipt.filter((_, i) => i !== index)
                                )
                              }
                              className="absolute bottom-0 right-0 w-full p-1 bg-F51D00 text-white text-xs invisible group-hover:visible rounded-b-md"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                </div>
                )}

                {/* Outside the entry fields, which are hidden once approved —
                    the decision must stay reversible, so Decline remains. */}
                {permissionFn("store_document_process")?.approver && (
                  <div className="px-5 xl:px-10 pb-4">
                    <ApproveDeclineActions
                      storeId={id}
                      approveKey="is_francise_fee_approved"
                      rejectKey="is_francise_fee_rejected"
                      // Payment can only be approved; there is no decline here.
                      showDecline={false}
                      record={initialValue}
                      isApproved={!!values?.is_francise_fee_approved}
                      isRejected={!!values?.is_francise_fee_rejected}
                      disabled={disabled}
                      formValues={values}
                      savedValues={savedValues}
                      // onDecided mirrors the approval into the payment terms
                      // flag, so it is decision-owned too.
                      extraDecisionKeys={["is_payment_terms_approve"]}
                      isUploading={receiptLoading}
                      onDecided={async (data) => {
                        // Drives the entry fields immediately — no reload needed.
                        setPaymentApproved(!!data?.is_francise_fee_approved);
                        onProgress({
                          is_francise_fee_approved: !!data?.is_francise_fee_approved,
                        });
                        await setFieldValue(
                          "is_francise_fee_approved",
                          !!data?.is_francise_fee_approved
                        );
                        await setFieldValue(
                          "is_francise_fee_rejected",
                          !!data?.is_francise_fee_rejected
                        );
                        await setFieldValue(
                          "is_payment_terms_approve",
                          !!data?.is_francise_fee_approved
                        );
                        // Share the decision with the other tabs: Store Details
                        // and LOI freeze while the payment is approved.
                        setStoreData((prev) => ({
                          ...prev,
                          payment: {
                            ...prev?.payment,
                            is_francise_fee_approved: !!data?.is_francise_fee_approved,
                            is_francise_fee_rejected: !!data?.is_francise_fee_rejected,
                            is_payment_terms_approve: !!data?.is_francise_fee_approved,
                          },
                        }));
                      }}
                    />
                  </div>
                )}

                <TabsComponent
                  handleClickfn={(item) => {
                    setCurrentTab(item);
                  }}
                  itemTabs={itemTabs}
                  setCurrentTab={setCurrentTab}
                  currentTab={currentTab}
                />
                {currentTab === "Payer Info" && (
                  <StorePayerInfoTab disabled={disabled} />
                )}
                {currentTab === "Payee Info" && (
                  <StorePayeeInfoTab disabled={disabled} />
                )}
                {currentTab === "Gst Details" && (
                  <StoreGstInfoTab disabled={disabled} />
                )}
                {currentTab === "Remarks" && (
                  <StoreRemarksInfoTab disabled={disabled} />
                )}
                {currentTab === "Payment History" && (
                  // The full instalment list lives on the store record the tab was
                  // seeded from — the same /store/getById response, not a new call.
                  <StorePaymentHistoryTab
                    payments={
                      Array.isArray(initialValue?.payment)
                        ? initialValue.payment
                        : []
                    }
                  />
                )}
              </div>

              {/* No Save once the payment is approved or the total is settled —
                  there is nothing further to record. */}
              {!entryDisabled && (
                <div
                  style={{
                    bottom: "0",
                  }}
                  className="flex justify-end px-10 mb-5x sticky bg-white py-4 invoice-bottom-container"
                >
                  <AddButton
                    onclkFn={() => {
                      if (Object.keys(errors).length !== 0) {
                        const currentIndex = itemTabs.indexOf(currentTab);
                        let nextTab;

                        if (currentIndex !== -1) {
                          // Go to the next tab based on the current tab index
                          nextTab =
                            currentIndex === itemTabs.length - 1
                              ? itemTabs[0] // Loop back to the first tab if the last tab is reached
                              : itemTabs[currentIndex + 1];

                          setCurrentTab(nextTab);
                        }
                      }
                    }}
                    prefixIcon={false}
                    text="Save"
                    type="submit"
                    loading={isSaving}
                  />
                </div>
              )}
            </div>
          </Form>
        );
      }}
    </Formik>
      <ImagePreviewModal
        isOpen={!!previewSrc}
        onClose={() => setPreviewSrc(null)}
        src={previewSrc}
      />
    </>
  );
};

export default StorePaymentTab;

const paymentTypeList = [
  { label: "Online", value: "online" },
  { label: "Bank Transfer", value: "bank_transfer" },
  { label: "Cheque", value: "cheque" },
  { label: "DD", value: "dd" },
];
const paymentStatusList = [
  { label: "Pending", value: "pending" },
  { label: "Paid", value: "paid" },
  { label: "Partial", value: "partial" },
];
const itemTabs = ["Payer Info", "Payee Info", "Gst Details", "Remarks", "Payment History"];